"use server";

import { cookies, headers } from "next/headers";
import { redirect } from "next/navigation";
import { IMPERSONATION_COOKIE, SESSION_COOKIE } from "@/lib/session";
import { resolveTenantSlug } from "@/lib/tenant-slug";

const BASE = process.env.BACKEND_INTERNAL_URL ?? "http://erp-backend:4000";
const REMEMBER_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

export type LoginResult = {
  ok: false;
  error: string;
  captchaRequired?: boolean;
};

export async function loginAction(
  email: string,
  password: string,
  remember: boolean,
  tenantSlugField?: string,
  altcha?: string,
): Promise<LoginResult> {
  // Host subdomain wins over the form field, which wins over 'default' — see
  // resolveTenantSlug for the full precedence contract.
  const host = (await headers()).get("host");
  const tenantSlug = resolveTenantSlug(host, tenantSlugField);

  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email,
      password,
      remember,
      tenantSlug,
      ...(altcha ? { altcha } : {}),
    }),
    cache: "no-store",
  });

  if (!res.ok) {
    if (res.status === 401) {
      const body = (await res.json().catch(() => null)) as {
        code?: string;
      } | null;
      if (body?.code === "CAPTCHA_REQUIRED") {
        return {
          ok: false,
          error: "Ze względów bezpieczeństwa potwierdź, że nie jesteś robotem.",
          captchaRequired: true,
        };
      }
      return { ok: false, error: "Nieprawidłowy e-mail lub hasło." };
    }
    if (res.status === 403) return { ok: false, error: "Konto jest nieaktywne." };
    return { ok: false, error: "Logowanie nie powiodło się. Spróbuj ponownie." };
  }

  const { token } = (await res.json()) as { token: string };
  const jar = await cookies();
  jar.set(SESSION_COOKIE, token, {
    httpOnly: true,
    secure: true,
    sameSite: "lax",
    path: "/",
    ...(remember ? { maxAge: REMEMBER_MAX_AGE } : {}),
  });
  // A fresh login must never inherit a previous impersonation context.
  jar.delete(IMPERSONATION_COOKIE);
  redirect("/panel");
}

export async function logoutAction(): Promise<void> {
  const jar = await cookies();
  jar.delete(SESSION_COOKIE);
  // A stale impersonation token must not survive logout/re-login — authHeader
  // prefers it over the session cookie, so leaving it would silently keep the
  // next session acting inside the impersonated tenant until the 1h TTL.
  jar.delete(IMPERSONATION_COOKIE);
  redirect("/login");
}
